import "./Header.scss";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import "moment/locale/ru";
import moment from "moment-timezone";
import DeviceIcons from "../DeviceIcons/DeviceIcon";
import { useNavigate } from "react-router-dom";
import { LoginMenu } from "../../auth/LoginMenu/LoginMenu";
import {Button, IconButton, Message, toaster, Tooltip, Whisper} from "rsuite";
import AdminComponent from "../../auth/AdminComponent/AdminComponent";
import { getTooltip } from "../../../shared/Tooltip";
import { SharedApi } from "../../../services/Shared.api";
import { PrimaryClockApi } from "../../../services/PrimaryClock.api";
import { getStandConfig } from "../../../shared/Stand";
import {AiFillToolIcon, AiOutlineMenuIcon, FaUsersIcon} from "../../../models/Icons.model";
import {DeviceData, DevicePageLayoutProps, DevicesListLayoutProps, HeaderProps} from "./Header.model";
import { getDeviceTabConfig } from "../Tabs/deviceTabsConfig";
import AiAssistantModal from "../AiAssistant/AiAssistantModal";
import {AiAssistantIcon} from "../../../models/Icons.model";

const AssistantHeaderAction = ({onClick, className = "me-3"}: {onClick: () => void; className?: string}) => (
  <Whisper speaker={<Tooltip>Помощник</Tooltip>}>
    <span className={className}>
      <AiAssistantIcon
        onClick={onClick}
        className="users assistant-header-icon"
        role="button"
        tabIndex={0}
        aria-label="Открыть помощника"
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") onClick();
        }}
      />
    </span>
  </Whisper>
);

type MobileSidebarMenuProps = {
  menuTabs: Array<{ key: string | number; label: string }>;
  onTabClick: (tabKey: string | number) => void;
  onFirmwaresClick: () => void;
  onUsersClick: () => void;
  activeTabKey: string;
  isFirmwaresPage: boolean;
  isUsersPage: boolean;
};

const MobileSidebarMenu = React.memo(({
  menuTabs,
  onTabClick,
  onFirmwaresClick,
  onUsersClick,
  activeTabKey,
  isFirmwaresPage,
  isUsersPage
}: MobileSidebarMenuProps) => (
  <div
    className="offcanvas offcanvas-end d-md-none"
    tabIndex={-1}
    id="headerSidebarMenu"
    aria-labelledby="headerSidebarMenuLabel"
  >
    <div className="offcanvas-header">
      <h5 className="offcanvas-title" id="headerSidebarMenuLabel">
        Разделы
      </h5>
      <button
        type="button"
        className="btn-close"
        data-bs-dismiss="offcanvas"
        data-bs-target="#headerSidebarMenu"
        aria-label="Close"
      ></button>
    </div>
    <div className="offcanvas-body d-md-flex flex-column p-0 pt-lg-3 overflow-y-auto">
      <nav className="tab-list-wrapper">
        <ul className="tab-list" role="tablist" aria-orientation="horizontal">
          {menuTabs.map((tab) => (
            <li key={`tab-${tab.key}`}>
              <button
                data-bs-dismiss="offcanvas"
                data-bs-target="#headerSidebarMenu"
                aria-label="Close"
                onClick={() => onTabClick(tab.key)}
                className={`btn tab-btn ${String(tab.key) === activeTabKey ? "active" : ""}`}
              >
                {tab.label}
              </button>
            </li>
          ))}
          <AdminComponent>
            {() => (
              <>
                <li>
                  <button
                    type="button"
                    onClick={onFirmwaresClick}
                    data-bs-dismiss="offcanvas"
                    data-bs-target="#headerSidebarMenu"
                    className={`btn tab-btn tab-action-btn ${isFirmwaresPage ? "active" : ""}`}
                  >
                    Прошивки
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={onUsersClick}
                    data-bs-dismiss="offcanvas"
                    data-bs-target="#headerSidebarMenu"
                    className={`btn tab-btn tab-action-btn ${isUsersPage ? "active" : ""}`}
                  >
                    Пользователи
                  </button>
                </li>
              </>
            )}
          </AdminComponent>
          <LoginMenu
            showLabel
            hideIcon
            className="btn tab-btn tab-action-btn tab-logout-btn"
          />
        </ul>
      </nav>
    </div>
  </div>
));

const Header = ({ route }: HeaderProps) => {
  const [ntpTime, setNtpTime] = useState("");
  const [deviceGuid, setDeviceGuid] = useState<string | null>(null);
  const [deviceData, setDeviceData] = useState<DeviceData | null>(null);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const standConfig = useMemo(() => getStandConfig(), []);
  const navigate = useNavigate();

  const handleLogoClick = useCallback(() => {
    navigate("/devices");
  }, [navigate]);

  const handleUsersClick = useCallback(() => {
    navigate("/users");
  }, [navigate]);

  const handleFirmwaresClick = useCallback(() => {
    navigate("/firmwares");
  }, [navigate]);

  const handleAssistantClick = useCallback(() => {
    setAssistantOpen(true);
  }, []);

  const getFormattedTime = useCallback(() => {
    const now = moment();
    const userTimeZone = moment.tz.guess();
    const offset = moment.tz(userTimeZone).utcOffset() / 60;
    const offsetSign = offset >= 0 ? "+" : "-";

    return `${now.format("DD.MM.YYYY HH:mm:ss")} ${offsetSign}${Math.abs(offset)}`;
  }, []);

  const getDeviceFullInfo = useCallback(async (guid: string) => {
    try {
      const [deviceInfo, deviceStatus] = await Promise.all([
        SharedApi.getControlBlockInfo(guid),
        SharedApi.getControlBlocksStatus(guid)
      ]);

      setDeviceData({
        ...deviceInfo,
        isConnected: deviceStatus?.isConnected || false,
        icons: {
          hasSimCard: deviceStatus?.status?.hasSimCard || false,
          hasBigLineDevices: deviceStatus?.status?.hasBigLineDevices || false,
          hasUsers: deviceStatus?.status?.hasUsers || false,
          isEthernetConnected: deviceStatus?.status?.isEthernetConnected || false,
          isWiFiConnected: deviceStatus?.status?.isWiFiConnected || false,
          environment: deviceStatus?.status?.environment || ""
        }
      });
    } catch (error) {
      console.error("Error fetching device data:", error);
      setDeviceData(null);
    }
  }, []);

  useEffect(() => {
    setNtpTime(getFormattedTime());

    const timer = setInterval(() => {
      setNtpTime(getFormattedTime());
    }, 1000);

    return () => clearInterval(timer);
  }, [getFormattedTime]);

  useEffect(() => {
    const viewport = window.visualViewport;

    if (!viewport) {
      return;
    }

    const root = document.documentElement;
    const updateVisualViewportInset = () => {
      const hiddenRightEdge = Math.max(
        0,
        root.clientWidth - viewport.offsetLeft - viewport.width
      );

      root.style.setProperty(
        "--header-visual-viewport-right",
        `${Math.ceil(hiddenRightEdge)}px`
      );
    };

    updateVisualViewportInset();
    viewport.addEventListener("resize", updateVisualViewportInset);
    viewport.addEventListener("scroll", updateVisualViewportInset);
    window.addEventListener("resize", updateVisualViewportInset);

    return () => {
      viewport.removeEventListener("resize", updateVisualViewportInset);
      viewport.removeEventListener("scroll", updateVisualViewportInset);
      window.removeEventListener("resize", updateVisualViewportInset);
      root.style.removeProperty("--header-visual-viewport-right");
    };
  }, []);

  useEffect(() => {
    const extractDeviceGuid = () => {
      const pathname = route?.split("?")[0] || "";

      if (pathname.startsWith("/devices/")) {
        const parts = pathname.split("/");
        if (parts.length >= 3) {
          const guid = parts[2];
          if (guid && guid.length > 10) {
            return guid;
          }
        }
      }
      return null;
    };

    const guid = extractDeviceGuid();
    setDeviceGuid(guid);

    if (guid) {
      getDeviceFullInfo(guid);
    } else {
      setDeviceData(null);
    }
  }, [route, getDeviceFullInfo]);

  const handleUpdate = () => {
    window.location.reload();
  };

  const pathname = route?.split("?")[0] || "";
  const isDevicePage = Boolean(pathname.startsWith("/devices/") && deviceGuid);
  const isDevicesListPage = pathname === "/devices";
  const isUsersPage = Boolean(pathname.startsWith("/users"));
  const isFirmwaresPage = Boolean(pathname.startsWith("/firmwares"));
  const showMenuButton = isDevicePage || isUsersPage || isFirmwaresPage;
  const menuTarget = "#headerSidebarMenu";
  const lastDeviceId = localStorage.getItem("lastDeviceId");
  const lastIsClockStation = localStorage.getItem("lastDeviceIsClockStation");
  const menuDeviceId = isDevicePage ? deviceGuid : lastDeviceId;
  const menuIsClockStation = isDevicePage
    ? Boolean(deviceData?.clockStation)
    : lastIsClockStation === "true";
  const menuIsAdmin = Boolean(JSON.parse(localStorage.getItem("user") || "null")?.isAdmin);
  const menuTabs = useMemo(
    () => getDeviceTabConfig(menuIsClockStation, menuIsAdmin).filter((t) => t.condition),
    [menuIsClockStation, menuIsAdmin]
  );
  const activeMenuTabKey = useMemo(() => {
    if (!pathname.startsWith("/devices/")) {
      return "";
    }

    const routeValue = route || "";
    const params = new URLSearchParams(routeValue.split("?")[1] || "");
    return params.get("tab") || "main";
  }, [pathname, route]);

  const handleMenuTabClick = useCallback((tabKey: string | number) => {
    if (!menuDeviceId) {
      navigate("/devices");
      return;
    }
    const url = tabKey === "main"
      ? `/devices/${menuDeviceId}`
      : `/devices/${menuDeviceId}?tab=${tabKey}`;
    navigate(url);
  }, [menuDeviceId, navigate]);

  const handleTimeSyncNow = async () => {
    try {
      const syncResult = await PrimaryClockApi.synchronizeTime(deviceGuid as string, 2);

      if (!syncResult) {
        toaster.push(
          <Message showIcon type="warning" closable>
            Синхронизация времени не удалась
          </Message>,
          { placement: "topEnd" }
        );
        return;
      }

      const result = await SharedApi.timeSyncNow(deviceGuid as string);

      if (result) {
        toaster.push(
          <Message showIcon type="success" closable>
            Синхронизация времени выполнена успешно
          </Message>,
          { placement: "topEnd" }
        );
      } else {
        toaster.push(
          <Message showIcon type="warning" closable>
            Время синхронизировано, но SharedApi вернул false
          </Message>,
          { placement: "topEnd" }
        );
      }
    } catch (error) {
      console.error("Ошибка при синхронизации времени:", error);
      toaster.push(
        <Message showIcon type="error" closable>
          Ошибка при синхронизации времени
        </Message>,
        { placement: "topEnd" }
      );
    }
  };

  return (
    <>
    <header className={`header-bg ${standConfig.headerClassName}`}>
      <svg xmlns="http://www.w3.org/2000/svg" className="d-none">
        <symbol id="list" viewBox="0 0 16 16">
          <path
            fillRule="evenodd"
            d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5z"
          />
        </symbol>
      </svg>
      <div className="container">
        {isDevicesListPage ? (
          <DevicesListLayout
            ntpTime={ntpTime}
            logoSrc={standConfig.logoSrc}
            handleTimeSyncNow={handleTimeSyncNow}
            onLogoClick={handleLogoClick}
            handleFirmwaresClick={handleFirmwaresClick}
            handleUsersClick={handleUsersClick}
            handleAssistantClick={handleAssistantClick}
            showMenuButton={showMenuButton}
            menuTarget={menuTarget}
          />
        ) : (
          <DevicePageLayout
            ntpTime={ntpTime}
            deviceData={deviceData}
            handleUpdate={handleUpdate}
            isDevicePage={isDevicePage}
            logoSrc={standConfig.logoSrc}
            onLogoClick={handleLogoClick}
            handleFirmwaresClick={handleFirmwaresClick}
            handleUsersClick={handleUsersClick}
            handleAssistantClick={handleAssistantClick}
            handleTimeSyncNow={handleTimeSyncNow}
            showMenuButton={showMenuButton}
            menuTarget={menuTarget}
          />
        )}
        {showMenuButton && (
          <MobileSidebarMenu
            menuTabs={menuTabs}
            onTabClick={handleMenuTabClick}
            onFirmwaresClick={handleFirmwaresClick}
            onUsersClick={handleUsersClick}
            activeTabKey={activeMenuTabKey}
            isFirmwaresPage={isFirmwaresPage}
            isUsersPage={isUsersPage}
          />
        )}
      </div>
    </header>
    <AiAssistantModal
      open={assistantOpen}
      onClose={() => setAssistantOpen(false)}
      deviceId={menuDeviceId}
      currentRoute={route || ""}
      isClockStation={menuDeviceId ? menuIsClockStation : undefined}
      hasMelody={isDevicePage ? Boolean(deviceData?.clockStation?.withMelody) : undefined}
    />
    </>
  );
};

const renderMobileDateTime = (ntpTime: string) => {
  const [date = "", time = "", offset = ""] = ntpTime.split(" ");

  return (
    <span className="ntp-time__mobile">
      <span className="ntp-time__date">{date}</span>
      <span className="ntp-time__time">{`${time}${offset ? ` ${offset}` : ""}`}</span>
    </span>
  );
};

const DevicesListLayout = ({
  ntpTime,
  logoSrc,
  onLogoClick,
  handleFirmwaresClick,
  handleUsersClick,
  handleAssistantClick,
  handleTimeSyncNow,
  showMenuButton,
  menuTarget
}: DevicesListLayoutProps & { showMenuButton: boolean; menuTarget: string }) => (
  <div className="row align-items-center header-layout">
    {/* Мобильная версия */}
    <div className="col-4 d-md-none header-mobile-column">
      <div className="d-flex justify-content-start">
        <Whisper speaker={<Tooltip>К списку устройств</Tooltip>}>
          <img
            onClick={onLogoClick}
            src={logoSrc}
            alt="BigClock Logo"
            className="header-logo"
          />
        </Whisper>
      </div>
    </div>

    <div className="col-4 d-md-none header-mobile-column">
      <div className="text-center">
        <Whisper
          placement="bottom"
          controlId="control-id-hover"
          trigger="hover"
          speaker={getTooltip("Синхронизация времени БКС от NTP сервера")}
        >
          <p className="m-0 ntp-time" onClick={handleTimeSyncNow}>
            {renderMobileDateTime(ntpTime)}
          </p>
        </Whisper>
      </div>
    </div>

    <div className="col-4 d-md-none header-mobile-column">
      <div className="d-flex justify-content-end align-items-center header-mobile-actions">
        {!showMenuButton && (
          <AdminComponent>
            {() => (
              <Whisper speaker={<Tooltip>Прошивки</Tooltip>}>
                <span className="me-2">
                  <AiFillToolIcon onClick={handleFirmwaresClick} className="users" />
                </span>
              </Whisper>
            )}
          </AdminComponent>
        )}
        <AssistantHeaderAction onClick={handleAssistantClick} className="me-2 header-assistant-action" />
        {!showMenuButton && (
          <AdminComponent>
            {() => (
              <Whisper speaker={<Tooltip>Пользователи</Tooltip>}>
                <span className="me-2">
                  <FaUsersIcon onClick={handleUsersClick} className="users" />
                </span>
              </Whisper>
            )}
          </AdminComponent>
        )}
        {showMenuButton ? (
          <Whisper speaker={<Tooltip>Разделы</Tooltip>}>
            <IconButton
              className="header-menu-button"
              style={{fontSize: 24}}
              icon={<AiOutlineMenuIcon />}
              size="sm"
              appearance="subtle"
              data-bs-toggle="offcanvas"
              data-bs-target={menuTarget}
              aria-controls="headerSidebarMenu"
              aria-expanded="false"
              aria-label="Toggle navigation"
            />
          </Whisper>
        ) : (
          <Whisper speaker={<Tooltip>Выйти</Tooltip>}>
            <span>
              <LoginMenu />
            </span>
          </Whisper>
        )}
      </div>
    </div>

    {/* Десктопная версия */}
    <div className="col-12 col-md-3 col-lg-3 d-none d-md-block">
      <div className="d-flex justify-content-center justify-content-md-start">
        <Whisper speaker={<Tooltip>К списку устройств</Tooltip>}>
          <img
            onClick={onLogoClick}
            src={logoSrc}
            alt="BigClock Logo"
            className="header-logo"
          />
        </Whisper>
      </div>
    </div>

    <div className="col-12 col-md-6 col-lg-6 d-none d-md-block">
      <div className="text-center">
        <Whisper
          placement="bottom"
          controlId="control-id-hover"
          trigger="hover"
          speaker={getTooltip("Синхронизация времени БКС от NTP сервера")}
        >
          <Button onClick={handleTimeSyncNow} className="me-2">NTP</Button>
        </Whisper>
        <p className="m-0 ntp-time">
          {ntpTime}
        </p>
      </div>
    </div>

    <div className="col-12 col-md-3 col-lg-3 d-none d-md-block">
      <div className="d-flex justify-content-center justify-content-md-end align-items-center header-actions">
        <AdminComponent>
          {() => (
            <Whisper speaker={<Tooltip>Прошивки</Tooltip>}>
              <span className="me-3">
                <AiFillToolIcon onClick={handleFirmwaresClick} className="users" />
              </span>
            </Whisper>
          )}
        </AdminComponent>
        <AssistantHeaderAction onClick={handleAssistantClick} />
        <AdminComponent>
          {() => (
            <Whisper speaker={<Tooltip>Пользователи</Tooltip>}>
              <span className="me-3">
                <FaUsersIcon onClick={handleUsersClick} className="users" />
              </span>
            </Whisper>
          )}
        </AdminComponent>
        <Whisper speaker={<Tooltip>Выйти</Tooltip>}>
          <span>
            <LoginMenu />
          </span>
        </Whisper>
      </div>
    </div>
  </div>
);

const DevicePageLayout = ({
  ntpTime,
  deviceData,
  handleUpdate,
  isDevicePage,
  logoSrc,
  onLogoClick,
  handleFirmwaresClick,
  handleUsersClick,
  handleAssistantClick,
  handleTimeSyncNow,
  showMenuButton,
  menuTarget
}: DevicePageLayoutProps & { showMenuButton: boolean; menuTarget: string }) => (
  <div className="row align-items-center header-layout">
    {/* Мобильная версия*/}
    <div className="col-4 d-md-none header-mobile-column">
      <div className="d-flex justify-content-start">
        <Whisper speaker={<Tooltip>К списку устройств</Tooltip>}>
          <img
            onClick={onLogoClick}
            src={logoSrc}
            alt="BigClock Logo"
            className="header-logo"
          />
        </Whisper>
      </div>
    </div>

    <div className="col-4 d-md-none header-mobile-column">
      <div className="text-center">
        <Whisper
          placement="bottom"
          controlId="control-id-hover"
          trigger="hover"
          speaker={getTooltip("Синхронизация времени БКС от NTP сервера")}
        >
          <p className="m-0 ntp-time" onClick={handleTimeSyncNow}>
            {renderMobileDateTime(ntpTime)}
          </p>
        </Whisper>
      </div>
    </div>

    <div className="col-4 d-md-none header-mobile-column">
      <div className="d-flex justify-content-end align-items-center header-mobile-actions">
        {!showMenuButton && (
          <AdminComponent>
            {() => (
              <Whisper speaker={<Tooltip>Прошивки</Tooltip>}>
                <span className="me-2">
                  <AiFillToolIcon onClick={handleFirmwaresClick} className="users" />
                </span>
              </Whisper>
            )}
          </AdminComponent>
        )}
        <AssistantHeaderAction onClick={handleAssistantClick} className="me-2 header-assistant-action" />
        {!showMenuButton && (
          <AdminComponent>
            {() => (
              <Whisper speaker={<Tooltip>Пользователи</Tooltip>}>
                <span className="me-2">
                  <FaUsersIcon onClick={handleUsersClick} className="users" />
                </span>
              </Whisper>
            )}
          </AdminComponent>
        )}
        {showMenuButton ? (
            <IconButton
              className="header-menu-button"
              style={{fontSize: 18}}
              icon={<AiOutlineMenuIcon />}
              size="sm"
              appearance="subtle"
              data-bs-toggle="offcanvas"
              data-bs-target={menuTarget}
              aria-controls="headerSidebarMenu"
              aria-expanded="false"
              aria-label="Toggle navigation"
            />
        ) : (
          <Whisper speaker={<Tooltip>Выйти</Tooltip>}>
            <span>
              <LoginMenu />
            </span>
          </Whisper>
        )}
      </div>
    </div>

    {/* Десктопная версия */}
    {isDevicePage ? (
      <>
        <div className="col-12 col-md-2 col-lg-2 d-none d-md-block">
          <div className="d-flex justify-content-center justify-content-md-start">
            <Whisper speaker={<Tooltip>К списку устройств</Tooltip>}>
              <img
                onClick={onLogoClick}
                src={logoSrc}
                alt="BigClock Logo"
                className="header-logo"
              />
            </Whisper>
          </div>
        </div>

        <div className="col-12 col-md-3 col-lg-3 d-none d-md-block">
          <div className="text-center text-md-start d-flex justify-content-end align-items-center">
            <Whisper
              placement="bottom"
              controlId="control-id-hover"
              trigger="hover"
              speaker={getTooltip("Синхронизация времени БКС от NTP сервера")}
            >
              <Button onClick={handleTimeSyncNow} className="me-2">NTP</Button>
            </Whisper>
            <p className="m-0 ntp-time">
              {ntpTime}
            </p>
          </div>
        </div>

        <div className="col-6 col-md-2 col-lg-2 d-none d-md-block">
          <div className="d-flex justify-content-center">
          </div>
        </div>

        <div className="col-6 col-md-3 col-lg-3 d-none d-md-block">
          <div className="d-flex justify-content-center flex-wrap">
            <DeviceIcons
              deviceData={deviceData}
              className="header-device-icons"
              showServiceAlerts
            />
          </div>
        </div>

        <div className="col-12 col-md-2 col-lg-2 d-none d-md-block">
          <div className="d-flex justify-content-center justify-content-md-end align-items-center header-actions">
            <AdminComponent>
              {() => (
                <Whisper speaker={<Tooltip>Прошивки</Tooltip>}>
                  <span className="me-3">
                    <AiFillToolIcon onClick={handleFirmwaresClick} className="users" />
                  </span>
                </Whisper>
              )}
            </AdminComponent>
            <AssistantHeaderAction onClick={handleAssistantClick} />
            <AdminComponent>
              {() => (
                <Whisper speaker={<Tooltip>Пользователи</Tooltip>}>
                  <span className="me-3">
                    <FaUsersIcon onClick={handleUsersClick} className="users" />
                  </span>
                </Whisper>
              )}
            </AdminComponent>
            <Whisper speaker={<Tooltip>Выйти</Tooltip>}>
              <span>
                <LoginMenu />
              </span>
            </Whisper>
          </div>
        </div>
      </>
    ) : (
      <>
        <div className="col-12 col-md-3 col-lg-3 d-none d-md-block">
          <div className="d-flex justify-content-center justify-content-md-start">
            <Whisper speaker={<Tooltip>К списку устройств</Tooltip>}>
              <img
                onClick={onLogoClick}
                src={logoSrc}
                alt="BigClock Logo"
                className="header-logo"
              />
            </Whisper>
          </div>
        </div>

        <div className="col-12 col-md-6 col-lg-6 d-none d-md-block">
          <div className="text-center">
            <Whisper
              placement="bottom"
              controlId="control-id-hover"
              trigger="hover"
              speaker={getTooltip("Синхронизация времени БКС от NTP сервера")}
            >
              <Button onClick={handleTimeSyncNow} className="me-2">NTP</Button>
            </Whisper>
            <p className="m-0 ntp-time">
              {ntpTime}
            </p>
          </div>
        </div>

        <div className="col-12 col-md-3 col-lg-3 d-none d-md-block">
          <div className="d-flex justify-content-center justify-content-md-end align-items-center header-actions">
            <AdminComponent>
              {() => (
                <Whisper speaker={<Tooltip>Прошивки</Tooltip>}>
                  <span className="me-3">
                    <AiFillToolIcon onClick={handleFirmwaresClick} className="users" />
                  </span>
                </Whisper>
              )}
            </AdminComponent>
            <AssistantHeaderAction onClick={handleAssistantClick} />
            <AdminComponent>
              {() => (
                <Whisper speaker={<Tooltip>Пользователи</Tooltip>}>
                  <span className="me-3">
                    <FaUsersIcon onClick={handleUsersClick} className="users" />
                  </span>
                </Whisper>
              )}
            </AdminComponent>
            <Whisper speaker={<Tooltip>Выйти</Tooltip>}>
              <span>
                <LoginMenu />
              </span>
            </Whisper>
          </div>
        </div>
      </>
    )}
  </div>
);

export default Header;
